import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * tailwind-merge has no built-in knowledge of tailwindcss-animate's utility
 * classes (used throughout libs/components/src/ui for enter/exit
 * animations), so by default it can't tell that e.g. `slide-in-from-left-0`
 * and `slide-in-from-left-1/2` both set the same CSS var and conflict.
 * Without this, overriding a component's default animation (e.g. Dialog's
 * center zoom-in) silently leaves both sets of classes applied, and which
 * one wins depends on Tailwind's arbitrary CSS output order.
 */
type AnimateClassGroupId =
  | "tw-animate-slide-in-x"
  | "tw-animate-slide-in-y"
  | "tw-animate-slide-out-x"
  | "tw-animate-slide-out-y"
  | "tw-animate-fade-in"
  | "tw-animate-fade-out"
  | "tw-animate-zoom-in"
  | "tw-animate-zoom-out"
  | "tw-animate-spin-in"
  | "tw-animate-spin-out";

const twMerge = extendTailwindMerge<AnimateClassGroupId>({
  extend: {
    classGroups: {
      "tw-animate-slide-in-x": [{ "slide-in-from-left": [() => true], "slide-in-from-right": [() => true] }],
      "tw-animate-slide-in-y": [{ "slide-in-from-top": [() => true], "slide-in-from-bottom": [() => true] }],
      "tw-animate-slide-out-x": [{ "slide-out-to-left": [() => true], "slide-out-to-right": [() => true] }],
      "tw-animate-slide-out-y": [{ "slide-out-to-top": [() => true], "slide-out-to-bottom": [() => true] }],
      "tw-animate-fade-in": [{ "fade-in": [() => true] }],
      "tw-animate-fade-out": [{ "fade-out": [() => true] }],
      "tw-animate-zoom-in": [{ "zoom-in": [() => true] }],
      "tw-animate-zoom-out": [{ "zoom-out": [() => true] }],
      "tw-animate-spin-in": [{ "spin-in": [() => true] }],
      "tw-animate-spin-out": [{ "spin-out": [() => true] }],
    },
  },
});

/**
 * Combines and merges CSS class names intelligently using clsx and tailwind-merge.
 * 
 * This utility function merges multiple class values into a single string, with Tailwind CSS
 * classes being intelligently merged to avoid conflicts. Later classes override earlier ones
 * when they conflict (e.g., 'px-2 px-4' becomes 'px-4').
 * 
 * @param inputs - Variable number of class values that can be strings, objects, arrays, or other clsx-compatible values
 * @returns A merged string of CSS class names with Tailwind conflicts resolved
 * 
 * @example
 * // Simple string concatenation
 * cn('text-red-500', 'font-bold')
 * // Returns: 'text-red-500 font-bold'
 * 
 * @example
 * // Conditional classes with objects
 * cn('base-class', { 'active-class': isActive, 'disabled-class': isDisabled })
 * // Returns: 'base-class active-class' (if isActive is true and isDisabled is false)
 * 
 * @example
 * // Tailwind class conflicts are resolved
 * cn('px-2 py-1', 'px-4')
 * // Returns: 'py-1 px-4' (px-4 overrides px-2)
 * 
 * @example
 * // Common usage in components
 * <div className={cn('default-styles', className, { 'error-styles': hasError })} />
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
